import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { isoToBritishInput, britishInputToIso, formatBritishTime } from '@/lib/date';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, ArrowLeft, X } from 'lucide-react';
import OrgSpeciesSearch, { type Species } from '@/components/OrgSpeciesSearch';
import { newRecordSchema } from '@tyto/shared';
import { useOrgContext } from '@/contexts/OrgContext';
import { useParams } from 'react-router';

type RecordFormValues = z.infer<typeof newRecordSchema>;

const OrgAddRecord: React.FC = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const { organisation } = useOrgContext();

  // Fetch species list for dropdown
  const { data: speciesList } = useQuery({
    queryKey: ['orgSpecies', organisation?.id],
    queryFn: async () => {
      const response = await fetch(`/api/org/${organisation?.id}/species`);
      if (!response.ok) {
        throw new Error('Failed to fetch species');
      }
      return response.json() as Promise<Species[]>;
    },
  });

  // Form setup
  const form = useForm<RecordFormValues>({
    resolver: zodResolver(newRecordSchema),
    defaultValues: {
      orgId: orgId,
      speciesId: '',
      note: '',
      locationNote: '',
      dtm: new Date().toISOString(),
      lat: undefined,
      lng: undefined,
      count: undefined,
    },
  });

  // Create record mutation
  const createRecordMutation = useMutation({
    mutationFn: async (data: RecordFormValues) => {
      const response = await fetch('/api/record', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create record');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationRecords'] });
      navigate('/app/records');
    },
  });

  // Form submission handler
  const onSubmit = (data: RecordFormValues) => {
    // Convert string values to numbers where needed
    const formattedData = {
      ...data,
      lat: data.lat !== undefined ? Number(data.lat) : undefined,
      lng: data.lng !== undefined ? Number(data.lng) : undefined,
      count: data.count !== undefined ? Number(data.count) : undefined,
    };

    createRecordMutation.mutate(formattedData);
  };

  return (
    <div className="container mx-auto p-6">
      <PageTopNav pageName="Add Record" />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="speciesId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Species</FormLabel>
                <FormControl>
                  <OrgSpeciesSearch
                    orgId={orgId}
                    onSelect={(species: Species) => {
                      setSelectedSpecies(species);
                      field.onChange(species.speciesId || '');
                    }}
                    placeholder="Search for a species..."
                  />
                </FormControl>
                {selectedSpecies && (
                  <div className="mt-2 p-2 bg-secondary rounded-md flex justify-between items-center">
                    <div>
                      <p className="font-semibold">{selectedSpecies.englishName}</p>
                      <p className="text-sm italic">{selectedSpecies.scientificName}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSpecies(null);
                        field.onChange('');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dtm"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date and Time</FormLabel>
                <FormControl>
                  <Input
                    type="datetime-local"
                    value={isoToBritishInput(field.value)}
                    onChange={x => field.onChange(britishInputToIso(x.target.value))}
                  />
                </FormControl>
                <div className="text-xs text-muted-foreground mt-1">
                  {field.value && formatBritishTime(field.value)}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="count"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Count</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Number observed"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="locationNote"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Location Notes</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe the location if coordinates are not provided"
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observation Notes</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Additional notes about the observation"
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => navigate('/app/records')}>
          Cancel
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={createRecordMutation.isPending}
        >
          {createRecordMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Record
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default OrgAddRecord;
